import { ORDER_STATUS_META } from 'lib/constants';
import { humanizeEnum } from 'lib/formatters';

/**
 * Resolve display metadata (label + badge tone) for an order/item status.
 * Falls back to a humanised label with a neutral tone for unknown enum values.
 */
export function getOrderStatusMeta(status) {
  if (status && ORDER_STATUS_META[status]) return ORDER_STATUS_META[status];
  return { label: humanizeEnum(status) || 'Unknown', tone: 'neutral' };
}

/** Statuses for which a vendor may still reject an order item. */
const REJECTABLE_STATUSES = new Set([
  'PENDING',
  'PLACED',
  'ACCEPTED',
  'CONFIRMED',
  'PREPARING',
  'READY_TO_PACK',
  'PAYMENT_CONFIRMED',
]);

export function canRejectItem(status) {
  return REJECTABLE_STATUSES.has(String(status || '').toUpperCase());
}

/**
 * Filter tabs for the orders screen, with the order-item statuses that fall
 * under each. Mirrors the mobile app's grouping.
 */
export const ORDER_TABS = [
  {
    key: 'received',
    label: 'Received',
    statuses: [
      'READY_TO_PACK',
      'PAYMENT_CONFIRMED',
      'PENDING',
      'PLACED',
      'CONFIRMED',
      'ACCEPTED',
      'PROCESSING',
      'PACKED',
      'PREPARING',
    ],
  },
  {
    key: 'ready',
    label: 'Ready',
    statuses: ['READY_FOR_DELIVERY', 'READY_FOR_PICKUP'],
  },
  {
    key: 'delivered',
    label: 'Delivered',
    statuses: ['DELIVERED', 'PICKED_UP', 'OUT_FOR_DELIVERY', 'SHIPPED', 'COMPLETED'],
  },
  {
    key: 'returned',
    label: 'Returned',
    statuses: [
      'CANCELLED',
      'CANCELLED_BY_VENDOR',
      'CANCELLED_BY_CUSTOMER',
      'REJECTED',
      'MARKED_FOR_RETURN',
      'RETURNED',
      'RETURN_PICKED_UP',
      'RETURN_IN_TRANSIT',
      'RETURN_DELIVERED',
      'RETURN_CANCELLED',
      'RETURN_COMPLETED',
      'RETURN_FAILED',
      'RETURNED_WITHOUT_PRODUCT',
      'MARKED_FOR_REFUND',
      'REFUND_PENDING',
      'REFUND_COMPLETED',
      'REFUND_FAILED',
      'REFUNDED',
    ],
  },
];

const STATUS_TO_TAB = {};
ORDER_TABS.forEach((tab) => {
  tab.statuses.forEach((status) => {
    STATUS_TO_TAB[status] = tab.key;
  });
});

/** Which tab an item status belongs to (or null if it matches none). */
export function tabForStatus(status) {
  return STATUS_TO_TAB[String(status || '').toUpperCase()] || null;
}

/**
 * Flatten orders into individual line items, each paired with its parent order:
 * `[{ item, order }]`. The orders screen is item-centric (one card per item).
 */
export function flattenOrders(orders) {
  return (orders || []).flatMap((order) =>
    (order.items || []).map((item) => ({ item, order })),
  );
}
